import React, { useEffect, useState, useCallback } from 'react';
import OAuth2Login from 'react-simple-oauth2-login';
import { useNavigate } from 'react-router-dom';

const CreateAccount = () => {
  const navigate = useNavigate();
  const [clientIdMissing, setClientIdMissing] = useState(false);
  const [googleClientId, setGoogleClientId] = useState(null);
  const [configError, setConfigError] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  
  const provider = { 
    name: "Google", 
    baseUrl: "https://auth.prompteh.com/authorize",  // Redirect to server's authorize endpoint
    providerParam: "google" 
  };

  useEffect(() => {
    async function fetchConfig() {
      try {
        console.log('CreateAccount: Fetching configuration from auth-worker');
        setIsLoading(true);
        const response = await fetch('https://auth.prompteh.com/config');
        
        console.log('CreateAccount: Config response status:', response.status);
        
        if (!response.ok) {
          throw new Error(`Config API returned ${response.status}: ${response.statusText}`);
        }
        
        const config = await response.json();
        console.log('CreateAccount: Config retrieved successfully');
        
        if (!config.googleClientId) {
          setClientIdMissing(true);
          setConfigError('Google Client ID not provided by server');
          console.error('CreateAccount: Google Client ID missing from server config');
        } else {
          setGoogleClientId(config.googleClientId);
          console.log('CreateAccount: Google Client ID retrieved successfully');
        }
      } catch (error) {
        console.error('CreateAccount: Error fetching configuration:', error);
        setConfigError(`Failed to load configuration: ${error.message}`);
        setClientIdMissing(true);
      } finally {
        setIsLoading(false);
      }
    }
    
    fetchConfig();
  }, []);

  // Add state parameter generation
  const generateState = () => {
    const array = new Uint32Array(28);
    window.crypto.getRandomValues(array);
    return Array.from(array, dec => ('0' + dec.toString(16)).substr(-2)).join('');
  };

  // Fix authorization parameters - simplify since server handles most params
  const authParams = {
    response_type: 'code',
    client_id: googleClientId,
    redirect_uri: 'https://auth.prompteh.com/auth/google/callback',  // Still needed for OAuth2Login component
    scope: 'openid email profile',
    state: generateState(),  // Keep for client-side tracking if needed
    access_type: 'offline',
    prompt: 'consent',
    provider: 'google'  // Add provider parameter for server's /authorize endpoint
  };

  const onSuccess = useCallback((response) => {
    console.log('CreateAccount onSuccess called with response:', response);
    const { code } = response;

    if (code) {
      console.log('CreateAccount: Authorization code received, exchanging for token');
      console.log('CreateAccount: Sending POST request to https://auth.prompteh.com/token');
      
      fetch('https://auth.prompteh.com/token', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ code }),
      })
        .then(res => {
          console.log('CreateAccount: Token response status:', res.status);
          return res.json();
        })
        .then(data => {
          console.log('CreateAccount: Token response data:', data);
          
          if (data.error) {
            throw new Error(data.error_description || data.error || 'Token exchange failed');
          }
          
          console.log('CreateAccount: Token received successfully, storing in localStorage');
          localStorage.setItem('auth_token', data.token);
          
          console.log('CreateAccount: Fetching user data from https://auth.prompteh.com/me');
          return fetch('https://auth.prompteh.com/me', {
            headers: {
              'Authorization': `Bearer ${data.token}`,
            },
          });
        })
        .then(res => {
          console.log('CreateAccount: User data response status:', res.status);
          return res.json();
        })
        .then(userData => {
          console.log('CreateAccount: User data response:', userData);
          
          if (userData.error) {
            throw new Error(userData.error_description || userData.error || 'Failed to fetch user data');
          }
          
          console.log('CreateAccount: User data fetched successfully, storing in localStorage');
          localStorage.setItem('user', JSON.stringify(userData)); 
          console.log('Account created and user data fetched:', userData);
          
          console.log('CreateAccount: Navigating to home page');
          navigate('/'); 
          window.location.reload();
        })
        .catch(err => {
          console.error('CreateAccount authentication error:', err);
          console.error('CreateAccount error stack:', err.stack);
          alert(`Account creation failed: ${err.message}`);
        });
    } else {
      console.error('CreateAccount: No code received in onSuccess callback', response);
      alert('Account creation failed: Authorization code not received.');
    }
  }, [navigate]); // Dependency array includes navigate since it's used inside

  const onFailure = useCallback((response) => {
    console.error('CreateAccount onFailure called with response:', response);
    console.error('CreateAccount onFailure error details:', {
      error: response.error,
      description: response.error_description,
      stack: response.stack
    });
    alert(`Account creation failed: ${response.error_description || response.error || 'An unknown error occurred.'}`);
  }, []); // Empty dependency array since no external dependencies are used

  // Add missing handler for postMessage, memoized with useCallback
  const handleOAuthResponse = useCallback((event) => {
    const allowedOrigins = ['https://auth.prompteh.com'];
    if (!allowedOrigins.includes(event.origin)) return;
    
    console.log('CreateAccount: Received postMessage event:', event.data);
    
    if (event.data.type === 'oauth-response') {
      if (event.data.error) {
        console.error('CreateAccount: OAuth error from postMessage:', event.data.error);
        onFailure(event.data);
      } else {
        console.log('CreateAccount: OAuth success from postMessage:', event.data);
        onSuccess(event.data);
        
        // Store email if received
        if (event.data.email) {
          const userData = { email: event.data.email, tier: 'paid' };
          localStorage.setItem('user', JSON.stringify(userData));
        }
        
        // Manually navigate to account page after successful auth
        navigate('/account');
      }
    }
  }, [onFailure, onSuccess, navigate]); // Dependencies for useCallback

  // Use useEffect to attach and clean up the event listener
  useEffect(() => {
    window.addEventListener('message', handleOAuthResponse);
    return () => window.removeEventListener('message', handleOAuthResponse);
  }, [handleOAuthResponse]); // Include handleOAuthResponse in dependency array

  return (
    <div className="flex flex-col gap-4 max-w-md mx-auto mt-8 p-6 bg-white rounded-lg shadow-md">
      <h2 className="text-2xl font-semibold mb-4 text-center">Create Account with Prompteh</h2>
      <p className="text-gray-600 mb-6 text-center">Create a new account using your Google account.</p>
      
      <div className="flex flex-col gap-4">
        {isLoading ? (
          <div className="text-center p-4">
            <p>Loading authentication configuration...</p>
          </div>
        ) : clientIdMissing || configError ? (
          <div className="text-red-500 p-4 border border-red-300 rounded-lg">
            <h3 className="font-bold">Configuration Error</h3>
            <p>{configError || "The Google client ID is missing. Please contact the administrator."}</p>
          </div>
        ) : (
          <OAuth2Login
            authorizationUrl={provider.baseUrl}
            clientId={googleClientId}
            redirectUri={authParams.redirect_uri}
            responseType="code"
            scope={authParams.scope}
            extraParams={{
              state: authParams.state,
              access_type: authParams.access_type,
              prompt: authParams.prompt,
              provider: provider.providerParam  // Add provider parameter to extraParams
            }}
            onSuccess={onSuccess}
            onFailure={onFailure}
            isCrossOrigin={true}
            render={({ onClick }) => (
              <button 
                onClick={onClick}
                className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
              >
                Create Account with {provider.name}
              </button>
            )}
          />
        )}
      </div>
    </div>
  );
};

export default CreateAccount;
